// V4 — Mobile-first Members. Same data + write paths as Members.jsx,
// rendered as a clean searchable card list. Each member is a tap-
// friendly card with avatar circle, role/team chips, and an action
// row. Add/Edit form is reused from the desktop view through a
// render-prop slot.

import React, { useMemo, useState } from "react";
import {
  UserPlus, Pencil, Trash2, Search, BarChart3, Crown, Shield,
  Mail, Phone, X
} from "lucide-react";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import PlayerProfile from "../components/PlayerProfile";
import Avatar from "../components/Avatar";
import { api } from "../api";
import { canDo } from "../data";

const ROLE_LABEL = {
  PRESIDENT: "President", VICE_PRESIDENT: "VP",
  FINANCE_HEAD: "Finance", CRICKET_HEAD: "Cricket Head",
  VOLLEYBALL_HEAD: "Volleyball Head", CAPTAIN: "Captain",
  PLAYER: "Player", ADMIN: "Admin"
};
const ROLE_PRIORITY = {
  ADMIN: 0, PRESIDENT: 1, VICE_PRESIDENT: 2,
  CAPTAIN: 3, CRICKET_HEAD: 4, VOLLEYBALL_HEAD: 4,
  FINANCE_HEAD: 5, PLAYER: 9
};
function topRole(roles) {
  const filtered = (roles || []).filter(r => r !== "MEMBER");
  if (!filtered.length) return "PLAYER";
  return filtered.sort((a, b) => (ROLE_PRIORITY[a] ?? 99) - (ROLE_PRIORITY[b] ?? 99))[0];
}
function avatarText(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const FILTERS = [
  { key: "all",       label: "All",        match: () => true },
  { key: "active",    label: "Active",     match: u => u.active },
  { key: "leaders",   label: "Leaders",    match: u => (u.roles || []).some(r => ["PRESIDENT","VICE_PRESIDENT","CAPTAIN","CRICKET_HEAD","VOLLEYBALL_HEAD","FINANCE_HEAD","ADMIN"].includes(r)) },
  { key: "inactive",  label: "Inactive",   match: u => !u.active }
];

export default function MembersMobile({
  state, reload, user,
  openCreate, openEdit
}) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [profileFor, setProfileFor] = useState(null);

  const canCreate = canDo(user, "members", "create");
  const canUpdate = canDo(user, "members", "update");
  const canDelete = canDo(user, "members", "delete");

  const members = useMemo(() => {
    let pool = state.members;
    if (!isAllTeams) {
      const ids = new Set(activeTeam?.players || []);
      pool = pool.filter(m => ids.has(m.id));
    }
    const filterFn = FILTERS.find(f => f.key === filter)?.match || (() => true);
    const q = search.trim().toLowerCase();
    return pool
      .filter(filterFn)
      .filter(m => {
        if (!q) return true;
        return (
          (m.name  || "").toLowerCase().includes(q) ||
          (m.email || "").toLowerCase().includes(q) ||
          (m.phone || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  }, [state.members, activeTeam, isAllTeams, filter, search]);

  async function remove(m) {
    if (!confirm(`Deactivate member "${m.name}"? They can be reactivated from the database.`)) return;
    try {
      await api.deleteMember(m.id);
      await reload();
      toast.success("Member deactivated.");
    } catch (err) { toast.error(err.message || "Couldn't deactivate."); }
  }

  // Count by filter for chip badges
  const counts = useMemo(() => {
    const c = {};
    for (const f of FILTERS) {
      c[f.key] = state.members.filter(m => {
        if (!isAllTeams) {
          const ids = new Set(activeTeam?.players || []);
          if (!ids.has(m.id)) return false;
        }
        return f.match(m);
      }).length;
    }
    return c;
  }, [state.members, activeTeam, isAllTeams]);

  return (
    <div className="m-mem">
      <header className="m-mem-head">
        <div>
          <small>{isAllTeams ? "All Teams" : (activeTeam?.name || "Team")}</small>
          <h1>Members</h1>
        </div>
        {canCreate && (
          <button type="button" className="btn m-mem-addBtn" onClick={openCreate}>
            <UserPlus size={16}/> Add
          </button>
        )}
      </header>

      {/* Search */}
      <div className="m-mem-search">
        <Search size={16}/>
        <input
          placeholder="Search by name, email, phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Clear">
            <X size={16}/>
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="m-mem-filterRow">
        {FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            className={filter === f.key ? "m-mem-chip m-mem-chip--active" : "m-mem-chip"}
            onClick={() => setFilter(f.key)}
          >
            {f.label} <em>{counts[f.key]}</em>
          </button>
        ))}
      </div>

      {/* Result count */}
      <div className="m-mem-count">
        {members.length} {members.length === 1 ? "member" : "members"}
      </div>

      {/* Member cards */}
      {members.length === 0 ? (
        <div className="m-mem-empty">
          <p>No members match the current filter.</p>
        </div>
      ) : (
        <div className="m-mem-list">
          {members.map(m => {
            const teams       = state.teams.filter(t => t.players?.includes(m.id));
            const roles       = (m.roles || []).filter(r => r !== "MEMBER");
            const primaryRole = topRole(roles);
            const cricketTeams = teams.filter(t => t.sport === "Cricket");
            const profileTeam  = !isAllTeams && activeTeam?.sport === "Cricket"
              ? activeTeam
              : cricketTeams[0];
            const isLeader = ["PRESIDENT","VICE_PRESIDENT","CAPTAIN","CRICKET_HEAD","VOLLEYBALL_HEAD","FINANCE_HEAD","ADMIN"]
              .some(r => roles.includes(r));
            return (
              <article key={m.id} className={`m-mem-card${!m.active ? " m-mem-card--inactive" : ""}`}>
                <div className="m-mem-card-head">
                  <div className={`m-mem-avatar m-mem-avatar--${primaryRole.toLowerCase()}${m.avatarUpdatedAt ? " m-mem-avatar--photo" : ""}`}>
                    {m.avatarUpdatedAt ? (
                      // Real uploaded photo — Avatar fetches the blob
                      // authenticated and fills the pill container.
                      <Avatar
                        member={m}
                        size={48}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: 14,
                          background: "transparent"
                        }}
                      />
                    ) : (
                      avatarText(m.name)
                    )}
                    {isLeader && <Crown size={12} className="m-mem-avatar-crown"/>}
                  </div>
                  <div className="m-mem-card-name">
                    <b>{m.name}</b>
                    <small>
                      <Mail size={11}/>
                      <span>{m.email}</span>
                    </small>
                    {m.phone && (
                      <small>
                        <Phone size={11}/>
                        <span>{m.phone}</span>
                      </small>
                    )}
                  </div>
                  {!m.active && <span className="m-mem-statusPill m-mem-statusPill--inactive">Inactive</span>}
                </div>

                {/* Roles + teams */}
                {(roles.length > 0 || teams.length > 0) && (
                  <div className="m-mem-tags">
                    {roles.map(r => (
                      <span key={r} className={`m-mem-roleTag m-mem-roleTag--${r}`}>
                        {ROLE_LABEL[r] || r.replace("_"," ")}
                      </span>
                    ))}
                    {teams.map(t => (
                      <span key={t.id} className={`m-mem-teamTag m-mem-teamTag--${t.sport}`}>
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="m-mem-actions">
                  {profileTeam && (
                    <button
                      type="button"
                      className="dark"
                      onClick={() => setProfileFor({ user: m, team: profileTeam })}
                      title={`Cricket stats (${profileTeam.name})`}
                    >
                      <BarChart3 size={13}/> Stats
                    </button>
                  )}
                  {canUpdate && (
                    <button type="button" className="dark" onClick={() => openEdit(m)} title="Edit">
                      <Pencil size={13}/> Edit
                    </button>
                  )}
                  {canDelete && m.active && (
                    <button
                      type="button"
                      className="dark"
                      onClick={() => remove(m)}
                      title="Deactivate"
                      style={{color:"var(--red)", borderColor:"rgba(255,107,107,0.4)"}}
                    >
                      <Trash2 size={13}/>
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {profileFor && (
        <PlayerProfile
          user={profileFor.user}
          team={profileFor.team}
          currentUser={user}
          onClose={() => setProfileFor(null)}
        />
      )}
    </div>
  );
}
